import SelectBuscable from "@/shared/components/SelectBuscable";
import { formatFechaEnZona } from '@/shared/lib/dateUtils'
import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { useState, useMemo, useEffect, useCallback } from "react";
import {
  Search,
  CirclePlus,
  MessageCircle,
  ArrowLeftToLine,
  X,
  ArrowLeft,
  Save,
  Loader2,
  Wrench,
} from "lucide-react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { apiService } from '@/services/core/api.service';
import { almacenService, type AlertaMantenimiento } from '@/features/almacen/api/almacenService';
import { herramientasApi } from '@/features/almacen/api/herramientas.api';
import type { ToolResponseDto } from '@/features/almacen/api/herramientas.api';
import type { UserProfile } from '@/services/api';
import toast from "react-hot-toast";
import RegistrarEntradaForm from '@/features/almacen/components/RegistrarEntradaForm';
import FilterSelect from '@/shared/components/FilterSelect';
import { tabPorCargar } from "@/features/almacen/lib/tabsDeMantenimiento";
import { EsqueletoTabla } from "@/shared/components/Esqueleto";
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';
import { Badge } from "@/shared/components/Badge";

// ─── API types ────────────────────────────────────────────────────────────────

type TipoMantenimiento = "Preventivo" | "Correctivo";
type EstadoMantenimiento = "Reparado" | "En mantenimiento";

interface ApiMantenimientoRecord {
  id: string;
  type: string;
  destination: string;
  responsibleUserId: string;
  sentAt: string;
  returnedAt?: string | null;
  resultingStatus?: string | null;
  entryComment?: string | null;
  returnComment?: string | null;
  evidenceUrl1?: string | null;
  evidenceUrl2?: string | null;
  evidenceUrl3?: string | null;
}

interface PaginatedMant {
  data: ApiMantenimientoRecord[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

// ─── Row type (built from ToolResponseDto + optional maintenance data) ─────────

interface MantenimientoRow {
  toolId: string;
  herramienta: string;
  tipo: TipoMantenimiento | "Sin tipo";
  fechaEnvio: string;
  taller: string;
  responsable: string;
  estado: EstadoMantenimiento;
  codigoSerie: string;
  entryComment?: string | null;
  returnComment?: string | null;
}

// ─── Mapping ──────────────────────────────────────────────────────────────────

function mapToolToRow(dto: ToolResponseDto): MantenimientoRow {
  return {
    toolId: dto.id,
    herramienta: dto.name,
    tipo: "Sin tipo",
    fechaEnvio: "Sin fecha",
    taller: "Sin taller",
    responsable: dto.responsibleUserName ?? "Sin responsable",
    estado: "En mantenimiento",
    codigoSerie: dto.serialCode,
  };
}

function mapTipo(type: string): TipoMantenimiento {
  return type === "corrective" ? "Correctivo" : "Preventivo";
}

// ─── Dropdown ─────────────────────────────────────────────────────────────────

// ─── Badge ────────────────────────────────────────────────────────────────────

const EstadoBadge = ({ estado }: { estado: EstadoMantenimiento | string }) => (
  <Badge color={estado === "Reparado" ? "verde" : "amarillo"}>
    {estado === "Reparado" ? "Reparado" : "En mantenimiento"}
  </Badge>
);

// ─── Empty states ─────────────────────────────────────────────────────────────

const NoResultsState = () => (
  <div className="flex flex-col items-center justify-center py-16 gap-2">
    <p className="text-sm font-medium text-gray-500">Sin resultados</p>
    <p className="text-sm text-gray-400">Intenta con otros filtros.</p>
  </div>
);

const NoDataState = () => (
  <div className="flex flex-col items-center justify-center py-20 gap-3">
    <img
      src="/herramientas-empty-state.svg"
      alt="Sin registros"
      className="w-40 h-auto mb-2"
    />
    <p className="text-base font-semibold text-gray-700">
      Aún no hay datos para mostrar
    </p>
    <p className="text-sm text-gray-400">
      Registra una entrada de mantenimiento.
    </p>
  </div>
);

// ─── Comentarios ──────────────────────────────────────────────────────────────

const ComentariosModal = ({
  row,
  onClose,
}: {
  row: MantenimientoRow;
  onClose: () => void;
}) => {

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const hasEntry  = !!row.entryComment?.trim();
  const hasReturn = !!row.returnComment?.trim();
  const noComments = !hasEntry && !hasReturn;

  return (
    <CapaDeModal onClickFondo={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <h2 className="text-xl font-bold text-gray-900">Comentarios de mantenimiento</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Info herramienta */}
        <div className="mx-6 mb-5 rounded-xl border border-gray-200 px-6 py-4 grid grid-cols-3 gap-4">
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Herramienta</p>
            <p className="text-sm font-medium text-gray-800">{row.herramienta}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Num. de serie</p>
            <p className="text-sm font-medium text-gray-800">{row.codigoSerie}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Taller</p>
            <p className="text-sm font-medium text-gray-800">{row.taller}</p>
          </div>
        </div>

        {/* Comentarios */}
        <div className="px-6 pb-6 space-y-4 max-h-80 overflow-y-auto">
          {noComments ? (
            <p className="text-sm text-gray-400 text-center py-6">
              No hay comentarios registrados en este mantenimiento.
            </p>
          ) : (
            <>
              {hasEntry && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-bold text-gray-900">Comentario de entrada</span>
                  </div>
                  <div className="rounded-xl bg-gray-100 px-4 py-3 text-sm text-gray-700 leading-relaxed">
                    {row.entryComment}
                  </div>
                </div>
              )}
              {hasReturn && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-bold text-gray-900">Comentario de retorno</span>
                  </div>
                  <div className="rounded-xl bg-gray-100 px-4 py-3 text-sm text-gray-700 leading-relaxed">
                    {row.returnComment}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </CapaDeModal>
  );
};

// ─── Retorno Modal ────────────────────────────────────────────────────────────

const RetornoModal = ({
  row,
  onClose,
  onGuardar,
}: {
  row: MantenimientoRow;
  onClose: () => void;
  onGuardar: () => void;
}) => {
  const [estado, setEstado] = useState("");
  const [fecha, setFecha] = useState("");
  const [comentario, setComentario] = useState("");
  const [saving, setSaving] = useState(false);
  const [maintenanceId, setMaintenanceId] = useState<string | null>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  // Fetch latest active maintenance record to get maintenanceId
  useEffect(() => {
    const fetchMaintId = async () => {
      try {
        const res = await apiService.request<PaginatedMant>(
          `/almacen/herramientas/${row.toolId}/mantenimiento?limit=10&page=1`,
        );
        const records = res.data ?? [];
        // Find the latest active (not returned yet)
        const active = records.find((r) => !r.returnedAt) ?? records[0];
        if (active) setMaintenanceId(active.id);
      } catch {
        // silently ignore — saving will fail gracefully
      }
    };
    fetchMaintId();
  }, [row.toolId]);

  const estadoOptions: { label: string; value: string }[] = [
    { label: "Reparado", value: "good" },
    { label: "No reparado", value: "damaged" },
    { label: "Parcialmente reparado", value: "damaged" },
  ];

  const handleGuardar = async () => {
    if (!estado || !fecha || saving) return;
    setSaving(true);
    try {
      if (!maintenanceId)
        throw new Error("No se encontró registro de mantenimiento activo");
      const estadoValue =
        estadoOptions.find((o) => o.label === estado)?.value ?? "good";
      await apiService.request(
        `/almacen/herramientas/${row.toolId}/mantenimiento/${maintenanceId}/retorno`,
        {
          method: "POST",
          body: JSON.stringify({
            resultingStatus: estadoValue,
            returnedAt: fecha,
            ...(comentario && { returnComment: comentario }),
          }),
        },
      );
      toast.success("Retorno registrado");
      onGuardar();
    } catch (err: unknown) {
      const e = err as { message?: string | string[] };
      const msg = Array.isArray(e.message)
        ? e.message.join(" ")
        : (e.message ?? "Error al registrar retorno");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <CapaDeModal onClickFondo={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <h2 className="text-xl font-bold text-gray-900">Devolución a almacén</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="px-6 pb-6 space-y-4">
          {/* Tool info */}
          <div
            className="rounded-xl px-5 py-4 grid grid-cols-3 gap-4"
            style={{ border: "1px solid #E0E0E0" }}
          >
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Herramienta</p>
              <p className="text-sm font-medium text-gray-800">
                {row.herramienta}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Num. de serie</p>
              <p className="text-sm font-medium text-gray-800">
                {row.codigoSerie}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Taller</p>
              <p className="text-sm font-medium text-gray-800">{row.taller}</p>
            </div>
          </div>

          {/* Estado + Fecha */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1.5">
                Estado resultante
              </label>
              <SelectBuscable
                variante="formulario"
                value={estado}
                onChange={setEstado}
                placeholder="Seleccionar"
                placeholderDeBusqueda="Buscar estado…"
                opciones={estadoOptions.map((e) => ({ value: e.label, label: e.label }))}
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1.5">
                Fecha
              </label>
              <input
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#3B5998]"
                style={{
                  borderColor: "#E0E0E0",
                  backgroundColor: "#F9F9F9",
                  colorScheme: "light",
                }}
              />
            </div>
          </div>

          {/* Comentario */}
          <div>
            <label className="block text-sm text-gray-600 mb-1.5">
              Comentario
            </label>
            <textarea
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              rows={3}
              placeholder="------"
              className="w-full px-3 py-2.5 rounded-lg border text-sm text-gray-700 placeholder-gray-300 resize-none focus:outline-none focus:ring-2 focus:ring-[#3B5998]"
              style={{ borderColor: "#E0E0E0", backgroundColor: "#F9F9F9" }}
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className={estiloDeBoton('secundario')}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleGuardar}
              disabled={!estado || !fecha || saving}
              className={estiloDeBoton('primario')}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Guardar
            </button>
          </div>
        </div>
      </div>
    </CapaDeModal>
  );
};

// ─── Ver herramienta (detail view) ───────────────────────────────────────────

type HerramientaTab = "nueva" | "evidencia";

const VerHerramientaView = ({
  row,
  onBack,
  onRetornar,
  isHistorial,
}: {
  row: MantenimientoRow;
  onBack: () => void;
  onRetornar: () => void;
  isHistorial?: boolean;
}) => {
  const [activeTab, setActiveTab] = useState<HerramientaTab>("nueva");
  const [fotos, setFotos] = useState<string[]>([]);
  const [loadingFotos, setLoadingFotos] = useState(true);
  const [evidenceFotos, setEvidenceFotos] = useState<string[]>([]);
  const [loadingEvidencia, setLoadingEvidencia] = useState(true);
  // Listado histórico de mantenimiento de esta herramienta
  const [mantHist, setMantHist] = useState<(MantenimientoRow & { id: string })[]>(
    [],
  );
  const [loadingMantHist, setLoadingMantHist] = useState(true);
  // Modal de comentarios (entrada/retorno) del registro de mantenimiento
  const [notasRow, setNotasRow] = useState<MantenimientoRow | null>(null);

  useEffect(() => {
    const fetchFotos = async () => {
      setLoadingFotos(true);
      try {
        const tool = await herramientasApi.findOne(row.toolId);
        const urls = [tool.photoUrl1, tool.photoUrl2, tool.photoUrl3].filter(
          Boolean,
        ) as string[];
        setFotos(urls);
      } catch {
        // silently ignore
      } finally {
        setLoadingFotos(false);
      }
    };
    fetchFotos();
  }, [row.toolId]);

  // Última evidencia: fotos del registro de mantenimiento más reciente que
  // tenga evidencia. El mantenimiento es una entidad aparte de los movimientos,
  // por eso se consulta el endpoint de mantenimiento (no el de movimientos).
  useEffect(() => {
    const fetchEvidencia = async () => {
      setLoadingEvidencia(true);
      try {
        const res = await apiService.request<PaginatedMant>(
          `/almacen/herramientas/${row.toolId}/mantenimiento?limit=100&page=1`,
        );
        const conEvidencia = (res.data ?? [])
          .map((m) => ({
            fecha: new Date(m.sentAt).getTime(),
            urls: [m.evidenceUrl1, m.evidenceUrl2, m.evidenceUrl3].filter(
              (u): u is string => !!u,
            ),
          }))
          .filter((m) => m.urls.length > 0)
          .sort((a, b) => b.fecha - a.fecha);
        setEvidenceFotos(conEvidencia[0]?.urls ?? []);
      } catch {
        setEvidenceFotos([]);
      } finally {
        setLoadingEvidencia(false);
      }
    };
    fetchEvidencia();
  }, [row.toolId]);

  // Historial de mantenimiento de esta herramienta (todos los registros)
  useEffect(() => {
    const fetchMantHist = async () => {
      setLoadingMantHist(true);
      try {
        const [mRes, users] = await Promise.all([
          apiService.request<PaginatedMant>(
            `/almacen/herramientas/${row.toolId}/mantenimiento?limit=100&page=1`,
          ),
          apiService.request<
            { id: string; firstName: string; lastName: string }[]
          >("/users/public/all"),
        ]);
        const usersMap: Record<string, string> = {};
        for (const u of users) usersMap[u.id] = `${u.firstName} ${u.lastName}`;
        setMantHist(
          (mRes.data ?? [])
            .slice()
            .sort(
              (a, b) =>
                new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime(),
            )
            .map((m) => ({
              id: m.id,
              toolId: row.toolId,
              herramienta: row.herramienta,
              tipo: mapTipo(m.type),
              fechaEnvio: formatFechaEnZona(m.sentAt),
              taller: m.destination || "Sin taller",
              responsable: m.responsibleUserId
                ? (usersMap[m.responsibleUserId] ?? "Sin responsable")
                : "Sin responsable",
              estado: (m.returnedAt
                ? "Reparado"
                : "En mantenimiento") as EstadoMantenimiento,
              codigoSerie: row.codigoSerie,
              entryComment: m.entryComment ?? null,
              returnComment: m.returnComment ?? null,
            })),
        );
      } catch {
        setMantHist([]);
      } finally {
        setLoadingMantHist(false);
      }
    };
    fetchMantHist();
  }, [row.toolId]);

  return (
    <div className="mx-auto px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className={estiloDeBoton('secundario')}
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
          <h1 className="text-xl font-bold text-gray-900">Ver herramienta</h1>
        </div>
        {!isHistorial && (
          <button
            type="button"
            onClick={onRetornar}
            className={estiloDeBoton('primario')}
          >
            <ArrowLeftToLine className="w-4 h-4" />
            Devolver
          </button>
        )}
      </div>

      {/* Info card */}
      <div
        className="bg-white rounded-xl shadow-sm mb-5"
        style={{ border: "1px solid #E0E0E0" }}
      >
        <div
          className="flex items-center justify-between px-6 pt-5 pb-4"
          style={{ borderBottom: "1px solid #E0E0E0" }}
        >
          <h2 className="text-base font-semibold text-gray-800">
            Información general
          </h2>
          <EstadoBadge estado={row.estado} />
        </div>
        <div className="px-6 py-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-4 gap-x-6">
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Herramienta</p>
              <p className="text-sm font-medium text-gray-800">
                {row.herramienta}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-0.5">
                Núm. de serie
              </p>
              <p className="text-sm font-medium text-gray-800">
                {row.codigoSerie}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-0.5">
                Tipo de mantenimiento
              </p>
              <p className="text-sm font-medium text-gray-800">{row.tipo}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Taller / Destino</p>
              <p className="text-sm font-medium text-gray-800">{row.taller}</p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-y-4 gap-x-6">
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Fecha de envío</p>
              <p className="text-sm font-medium text-gray-800">
                {row.fechaEnvio}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Responsable</p>
              <p className="text-sm font-medium text-gray-800">
                {row.responsable}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs + avatar */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex gap-1">
          {(
            [
              { key: "nueva", label: "Fotografías" },
              { key: "evidencia", label: "Última evidencia" },
            ] as { key: HerramientaTab; label: string }[]
          ).map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              className={`px-4 py-2.5 text-sm font-semibold transition-colors border-b-2 ${
                activeTab === key
                  ? "border-[#477FFA]"
                  : "border-transparent hover:border-gray-200"
              }`}
              style={{ color: "#1E1E1F" }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div
        className="bg-white rounded-xl shadow-sm"
        style={{ border: "1px solid #E0E0E0" }}
      >
        <div
          className="px-6 pt-5 pb-4"
          style={{ borderBottom: "1px solid #E0E0E0" }}
        >
          <h2 className="text-base font-semibold text-gray-800">
            {activeTab === "nueva" ? "Fotografías" : "Última evidencia"}
          </h2>
        </div>
        <div className="px-6 py-5">
          {(() => {
            const esNueva = activeTab === "nueva";
            const cargando = esNueva ? loadingFotos : loadingEvidencia;
            const imgs = esNueva ? fotos : evidenceFotos;
            const vacio = esNueva
              ? "Sin fotos adjuntas"
              : "Sin evidencia reciente";
            if (cargando) {
              return (
                <div className="flex items-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              );
            }
            if (imgs.length === 0) {
              return (
                <p className="text-sm text-gray-400 italic">{vacio}</p>
              );
            }
            return (
              <div className="flex flex-wrap gap-3">
                {imgs.map((url, i) => (
                  <div
                    key={i}
                    className="w-40 h-32 rounded-xl overflow-hidden flex-shrink-0 bg-gray-50"
                    style={{ border: "1px solid #E0E0E0" }}
                  >
                    <img
                      src={url}
                      alt={`Foto ${i + 1}`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Historial de mantenimiento de esta herramienta */}
      <div
        className="bg-white rounded-xl shadow-sm mt-6"
        style={{ border: "1px solid #E0E0E0" }}
      >
        <div
          className="px-6 pt-5 pb-4"
          style={{ borderBottom: "1px solid #E0E0E0" }}
        >
          <h2 className="text-base font-semibold text-gray-800">
            Historial de mantenimiento
          </h2>
        </div>
        {loadingMantHist ? (
          <div className="flex items-center px-6 py-6">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        ) : mantHist.length === 0 ? (
          <p className="px-6 py-6 text-sm text-gray-400 italic">
            Sin registros de mantenimiento
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr
                  style={{
                    backgroundColor: "#FAF9FF",
                    borderBottom: "1px solid #E4E4E4",
                  }}
                >
                  {[
                    "Tipo",
                    "Fecha de envío",
                    "Taller",
                    "Responsable",
                    "Estado",
                    "Núm. de serie",
                  ].map((h) => (
                    <th
                      key={h}
                      className="text-left py-3 px-4 text-sm font-semibold first:pl-6 whitespace-nowrap"
                      style={{ color: "#534D59" }}
                    >
                      {h}
                    </th>
                  ))}
                  <th className="py-3 px-4 pr-6" />
                </tr>
              </thead>
              <tbody>
                {mantHist.map((m) => (
                  <tr key={m.id} style={{ borderBottom: "1px solid #E4E4E4" }}>
                    <td className="py-3 px-4 pl-6 text-gray-600 whitespace-nowrap">
                      {m.tipo}
                    </td>
                    <td className="py-3 px-4 text-gray-500 whitespace-nowrap">
                      {m.fechaEnvio}
                    </td>
                    <td className="py-3 px-4 text-gray-600 whitespace-nowrap">
                      {m.taller}
                    </td>
                    <td className="py-3 px-4 text-gray-700 whitespace-nowrap">
                      {m.responsable}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <EstadoBadge estado={m.estado} />
                    </td>
                    <td className="py-3 px-4 text-gray-500 whitespace-nowrap">
                      {row.codigoSerie || "Sin número de serie"}
                    </td>
                    <td className="py-3 px-4 pr-6 text-right whitespace-nowrap">
                      {m.entryComment?.trim() || m.returnComment?.trim() ? (
                        <button
                          type="button"
                          onClick={() => setNotasRow(m)}
                          className="inline-flex items-center gap-1.5 text-sm font-medium transition-colors hover:opacity-80"
                          style={{ color: "#315FB9" }}
                        >
                          <MessageCircle className="w-4 h-4" />
                          Ver notas
                        </button>
                      ) : (
                        <span className="text-sm text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {notasRow && (
        <ComentariosModal row={notasRow} onClose={() => setNotasRow(null)} />
      )}
    </div>
  );
};

// ─── Tabs ─────────────────────────────────────────────────────────────────────

type Tab = "mantenimiento" | "alertas" | "historial";

// ─── Alertas de mantenimiento ─────────────────────────────────────────────────

// Una alerta es una herramienta a la que le toca mantenimiento, todavia sin
// enviar a taller. Por eso tipo, fecha de envio, taller y responsable salen de
// su ultimo mantenimiento registrado: dicen cuando y donde se atendio la vez
// pasada. Si nunca ha ido, quedan vacios.
interface AlertaRow {
  toolId: string;
  herramienta: string;
  photoUrl: string | null;
  tipo: string;
  fechaEnvio: string;
  taller: string;
  responsable: string;
  estado: "Vencido" | "Por vencer";
  codigoSerie: string;
  detalle: string;
}

// Texto del porque de la alerta, con el mismo criterio que usa el dashboard.
const detalleAlerta = (a: AlertaMantenimiento): string => {
  if (a.horasAcumuladas != null && a.intervaloHoras != null) {
    return `${a.horasAcumuladas} / ${a.intervaloHoras} hrs de uso`;
  }
  if (a.diasRestantes != null) {
    const d = a.diasRestantes;
    if (d < 0) {
      const n = Math.abs(d);
      return `Venció hace ${n} ${n === 1 ? "día" : "días"}`;
    }
    if (d === 0) return "Vence hoy";
    return `Vence en ${d} ${d === 1 ? "día" : "días"}`;
  }
  return "—";
};

const AlertasTab = ({
  rows,
  loading,
  onRegistrar,
}: {
  rows: AlertaRow[];
  loading: boolean;
  onRegistrar: (toolId: string) => void;
}) => {
  const [search, setSearch] = useState("");
  const [filterTipo, setFilterTipo] = useState("");
  const [filterHerramienta, setFilterHerramienta] = useState("");
  const [filterTaller, setFilterTaller] = useState("");
  const [filterResponsable, setFilterResponsable] = useState("");
  const [filterEstado, setFilterEstado] = useState("");

  const unicos = (valores: string[]) =>
    [...new Set(valores.filter((v) => v && v !== "—"))];

  const visibles = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      const matchSearch =
        !q ||
        r.herramienta.toLowerCase().includes(q) ||
        r.codigoSerie.toLowerCase().includes(q);
      return (
        matchSearch &&
        (!filterTipo || r.tipo === filterTipo) &&
        (!filterHerramienta || r.herramienta === filterHerramienta) &&
        (!filterTaller || r.taller === filterTaller) &&
        (!filterResponsable || r.responsable === filterResponsable) &&
        (!filterEstado || r.estado === filterEstado)
      );
    });
  }, [rows, search, filterTipo, filterHerramienta, filterTaller, filterResponsable, filterEstado]);

  const limpiar = () => {
    setSearch("");
    setFilterTipo("");
    setFilterHerramienta("");
    setFilterTaller("");
    setFilterResponsable("");
    setFilterEstado("");
  };

  // A-01: silueta de la tabla de mantenimiento.
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <EsqueletoTabla filas={6} columnas={5} />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm py-16 text-center">
        <p className="text-sm text-gray-500">
          Ninguna herramienta tiene mantenimiento vencido o por vencer.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap items-center gap-3 mb-6">
        <div className="relative w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar en herramientas..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B5998]"
          />
        </div>
        <FilterSelect label="Tipo" options={(unicos(rows.map((r) => r.tipo))).map((o: string) => ({ value: o, label: o }))} value={filterTipo} onChange={setFilterTipo} ancho="contenido"
          />
        <FilterSelect label="Herramienta" options={(unicos(rows.map((r) => r.herramienta))).map((o: string) => ({ value: o, label: o }))} value={filterHerramienta} onChange={setFilterHerramienta} ancho="contenido"
          />
        <FilterSelect label="Taller" options={(unicos(rows.map((r) => r.taller))).map((o: string) => ({ value: o, label: o }))} value={filterTaller} onChange={setFilterTaller} ancho="contenido"
          />
        <FilterSelect label="Responsable" options={(unicos(rows.map((r) => r.responsable))).map((o: string) => ({ value: o, label: o }))} value={filterResponsable} onChange={setFilterResponsable} ancho="contenido"
          />
        <FilterSelect label="Estado" options={(["Vencido", "Por vencer"]).map((o) => ({ value: o, label: o }))} value={filterEstado} onChange={setFilterEstado} ancho="contenido"
          />
        <button
          type="button"
          onClick={limpiar}
          className="text-sm font-medium text-[#315FB9] hover:underline ml-auto"
        >
          Limpiar
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead style={{ backgroundColor: "#FAF9FF" }}>
              <tr>
                {["Herramienta", "Tipo", "Fecha de envío", "Taller", "Responsable", "Estado", "Num. de serie", "Acciones"].map((h) => (
                  <th key={h} className="px-6 py-3 text-left text-sm font-medium text-[#1B2128] whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {visibles.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <NoResultsState />
                  </td>
                </tr>
              ) : (
                visibles.map((r) => (
                  <tr key={r.toolId} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-11 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-gray-50 flex items-center justify-center"
                          style={{ border: "1px solid #E0E0E0" }}
                        >
                          {r.photoUrl ? (
                            <img src={r.photoUrl} alt={r.herramienta} className="w-full h-full object-contain" />
                          ) : (
                            <Wrench className="w-4 h-4 text-gray-300" />
                          )}
                        </div>
                        <span className="text-sm text-gray-800">{r.herramienta}</span>
                      </div>
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-600 whitespace-nowrap">{r.tipo}</td>
                    <td className="px-6 py-3 text-sm text-gray-600 whitespace-nowrap">{r.fechaEnvio}</td>
                    <td className="px-6 py-3 text-sm text-gray-600 whitespace-nowrap">{r.taller}</td>
                    <td className="px-6 py-3 text-sm text-gray-600 whitespace-nowrap">{r.responsable}</td>
                    <td className="px-6 py-3">
                      <Badge color={r.estado === "Vencido" ? "rojo" : "amarillo"} title={r.detalle}>
                        {r.estado}
                      </Badge>
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-600 whitespace-nowrap">{r.codigoSerie}</td>
                    <td className="px-6 py-3">
                      <button
                        type="button"
                        onClick={() => onRegistrar(r.toolId)}
                        className="text-sm font-medium text-[#315FB9] hover:underline whitespace-nowrap"
                      >
                        Registrar mantenimiento
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const MantenimientoAlmacen = () => {
  const navigate = useNavigate();
  const { search: locationSearch } = useLocation();
  // Enlaces desde el dashboard: ?tab=alertas abre ese tab y ?nuevo=<toolId>
  // abre el alta con la herramienta ya elegida.
  const params = new URLSearchParams(locationSearch);
  const tabParam = params.get("tab");
  const nuevoToolId = params.get("nuevo");
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>(
    tabParam === "alertas" || tabParam === "historial" ? (tabParam as Tab) : "mantenimiento",
  );
  const [comentariosRow, setComentariosRow] = useState<MantenimientoRow | null>(
    null,
  );
  const [retornoRow, setRetornoRow] = useState<MantenimientoRow | null>(null);

  // Data
  const [activeRows, setActiveRows] = useState<MantenimientoRow[]>([]);
  const [histRows, setHistRows] = useState<MantenimientoRow[]>([]);
  // Alertas de mantenimiento: el back ya las calcula (por horas de uso o por
  // calendario) y las devuelve completas dentro del dashboard. Aqui se listan
  // todas; el dashboard solo enseña las primeras.
  const [alertaRows, setAlertaRows] = useState<AlertaRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState("");
  const [filterTipo, setFilterTipo] = useState("");
  const [filterHerramienta, setFilterHerramienta] = useState("");
  const [filterTaller, setFilterTaller] = useState("");
  const [filterResponsable, setFilterResponsable] = useState("");
  const [filterEstado, setFilterEstado] = useState("");

  // ── Fetch maintenance record for a tool and merge into the row ──
  const enrichWithMaintRecord = async (
    row: MantenimientoRow,
    usersMap: Record<string, string>,
  ): Promise<MantenimientoRow> => {
    try {
      const res = await apiService.request<PaginatedMant>(
        `/almacen/herramientas/${row.toolId}/mantenimiento?limit=1&page=1`,
      );
      const records = res.data ?? [];
      const latest = records[0];
      if (!latest) return row;
      const responsable = latest.responsibleUserId
        ? (usersMap[latest.responsibleUserId] ?? "Sin responsable")
        : "Sin responsable";
      return {
        ...row,
        tipo:         mapTipo(latest.type),
        taller:       latest.destination ?? "Sin taller",
        fechaEnvio:   formatFechaEnZona(latest.sentAt),
        responsable,
        entryComment:  latest.entryComment  ?? null,
        returnComment: latest.returnComment ?? null,
      };
    } catch {
      return row;
    }
  };

  // ── Fetch ──
  // El back no tiene listado global de mantenimiento: todo cuelga de
  // /almacen/herramientas/:toolId/mantenimiento. Armar el historial obliga a
  // preguntar herramienta por herramienta, o sea hasta 100 peticiones. Antes se
  // hacian todas al entrar, aunque el tab visible fuera "En proceso" y no las
  // necesitara: de ahi la tardanza. Ahora la carga se parte en dos y el
  // historial solo se pide cuando de verdad se abre ese tab (o el de alertas,
  // que lo usa para sus columnas).
  const [histLoaded, setHistLoaded] = useState(false);
  const [histLoading, setHistLoading] = useState(false);
  // Alertas lleva su propio estado: antes compartia el del historial y abrirlo
  // obligaba a esperar el recorrido completo de las herramientas.
  const [alertasLoaded, setAlertasLoaded] = useState(false);
  const [alertasLoading, setAlertasLoading] = useState(false);
  const [tools, setTools] = useState<ToolResponseDto[]>([]);
  const [usersMap, setUsersMap] = useState<Record<string, string>>({});
  // La peticion base ya termino, haya o no herramientas. Es distinto de
  // `tools.length`: con el almacen vacio los dos tabs de abajo tienen que
  // resolverse igual, no quedarse cargando.
  const [baseLoaded, setBaseLoaded] = useState(false);

  const fetchBase = useCallback(async () => {
    setLoading(true);
    try {
      const [allTools, users] = await Promise.all([
        herramientasApi.findAll({ limit: 100 }),
        apiService.request<UserProfile[]>("/users/public/all"),
      ]);

      const mapa: Record<string, string> = {};
      for (const u of users) {
        mapa[u.id] = `${u.firstName} ${u.lastName}`;
      }
      setTools(allTools.data);
      setUsersMap(mapa);

      // Tab "En proceso": solo las que estan en mantenimiento ahora, que son
      // pocas, con una peticion cada una para su registro abierto.
      const inMaintTools = allTools.data.filter(
        (t) => t.status === "in_maintenance",
      );
      const enriched = await Promise.all(
        inMaintTools.map(mapToolToRow).map((r) => enrichWithMaintRecord(r, mapa)),
      );
      setActiveRows(enriched);
    } catch {
      toast.error("Error al cargar datos de mantenimiento");
    } finally {
      setLoading(false);
      setBaseLoaded(true);
    }
  }, []);

  /**
   * Ultimo mantenimiento de cada herramienta de la lista que se le pase.
   *
   * El back no tiene un listado global: todo cuelga de
   * /almacen/herramientas/:id/mantenimiento, asi que hay que preguntar una por
   * una. Se va en tandas para no lanzar cien peticiones de golpe.
   */
  const ultimoMantenimientoDe = useCallback(
    async (
      toolsList: ToolResponseDto[],
      mapa: Record<string, string>,
    ): Promise<MantenimientoRow[]> => {
      const TANDA = 10;
      const filas: MantenimientoRow[] = [];
      for (let i = 0; i < toolsList.length; i += TANDA) {
        const lote = await Promise.all(
          toolsList.slice(i, i + TANDA).map(async (t): Promise<MantenimientoRow | null> => {
            try {
              const res = await apiService.request<PaginatedMant>(
                `/almacen/herramientas/${t.id}/mantenimiento?limit=1&page=1`,
              );
              const latest = res.data?.[0];
              if (!latest) return null; // sin registros → no aparece en historial
              const responsable = latest.responsibleUserId
                ? (mapa[latest.responsibleUserId] ?? "Sin responsable")
                : "Sin responsable";
              return {
                toolId:        t.id,
                herramienta:   t.name,
                tipo:          mapTipo(latest.type),
                taller:        latest.destination ?? "Sin taller",
                fechaEnvio:    formatFechaEnZona(latest.sentAt),
                responsable,
                // Si ya fue retornada → "Reparado"; si sigue en mantenimiento → "En mantenimiento"
                estado:        latest.returnedAt ? "Reparado" : "En mantenimiento",
                codigoSerie:   t.serialCode,
                entryComment:  latest.entryComment  ?? null,
                returnComment: latest.returnComment ?? null,
              };
            } catch {
              return null;
            }
          }),
        );
        filas.push(...lote.filter((r): r is MantenimientoRow => r !== null));
      }
      return filas;
    },
    [],
  );

  /** Tab "Historial": recorre todas las herramientas. Es el mas caro. */
  const fetchHistorial = useCallback(
    async (toolsList: ToolResponseDto[], mapa: Record<string, string>) => {
      setHistLoading(true);
      try {
        setHistRows(await ultimoMantenimientoDe(toolsList, mapa));
      } finally {
        setHistLoaded(true);
        setHistLoading(false);
      }
    },
    [ultimoMantenimientoDe],
  );

  /**
   * Tab "Alertas": el listado completo, sin el recorte que hace el dashboard.
   *
   * Antes esperaba a que terminara el historial para reusar sus datos, asi que
   * abrir Alertas costaba recorrer las cien herramientas. Solo necesita el
   * ultimo mantenimiento de las que tienen alerta, que son un punado: tipo,
   * fecha de envio, taller y responsable salen de ahi, porque la alerta en si
   * todavia no ha ido a taller. El numero de serie sale de la herramienta.
   */
  const fetchAlertas = useCallback(
    async (toolsList: ToolResponseDto[], mapa: Record<string, string>) => {
      setAlertasLoading(true);
      try {
        const dash = await almacenService.getDashboardData();
        const alertas = dash.alertas ?? [];
        const toolById = new Map(toolsList.map((t) => [t.id, t]));
        const conAlerta = alertas
          .map((a) => toolById.get(a.id))
          .filter((t): t is ToolResponseDto => !!t);

        const ultimos = await ultimoMantenimientoDe(conAlerta, mapa);
        const ultimoPorTool = new Map(ultimos.map((r) => [r.toolId, r]));

        setAlertaRows(
          alertas
            .map((a): AlertaRow => {
              const ultimo = ultimoPorTool.get(a.id);
              return {
                toolId: a.id,
                herramienta: a.herramienta,
                photoUrl: a.photoUrl,
                tipo: ultimo?.tipo ?? "—",
                fechaEnvio: ultimo?.fechaEnvio ?? "—",
                taller: ultimo?.taller ?? "—",
                responsable: ultimo?.responsable ?? "—",
                estado: a.tipo === "vencido" ? "Vencido" : "Por vencer",
                codigoSerie: toolById.get(a.id)?.serialCode ?? ultimo?.codigoSerie ?? "—",
                detalle: detalleAlerta(a),
              };
            })
            // Vencidas primero: son las que ya deberian estar en taller.
            .sort((x, y) => (x.estado === y.estado ? 0 : x.estado === "Vencido" ? -1 : 1)),
        );
      } catch {
        setAlertaRows([]);
      } finally {
        setAlertasLoaded(true);
        setAlertasLoading(false);
      }
    },
    [ultimoMantenimientoDe],
  );

  // Recarga tras crear o retornar un mantenimiento: solo rehace los tabs que ya
  // se habian abierto, para no volver a pagar sus peticiones.
  const fetchData = useCallback(async () => {
    await fetchBase();
    if (!histLoaded && !alertasLoaded) return;
    const [allTools, users] = await Promise.all([
      herramientasApi.findAll({ limit: 100 }),
      apiService.request<UserProfile[]>("/users/public/all"),
    ]);
    const mapa: Record<string, string> = {};
    for (const u of users) mapa[u.id] = `${u.firstName} ${u.lastName}`;
    if (histLoaded) await fetchHistorial(allTools.data, mapa);
    if (alertasLoaded) await fetchAlertas(allTools.data, mapa);
  }, [fetchBase, fetchHistorial, fetchAlertas, histLoaded, alertasLoaded]);

  useEffect(() => {
    fetchBase();
  }, [fetchBase]);

  // Cada tab pide lo suyo la primera vez que se abre, por separado.
  useEffect(() => {
    const porCargar = tabPorCargar(activeTab, {
      baseLista: baseLoaded,
      historialCargado: histLoaded,
      historialCargando: histLoading,
      alertasCargadas: alertasLoaded,
      alertasCargando: alertasLoading,
    });
    if (porCargar === "historial") fetchHistorial(tools, usersMap);
    if (porCargar === "alertas") fetchAlertas(tools, usersMap);
  }, [activeTab, baseLoaded, histLoaded, histLoading, alertasLoaded, alertasLoading, tools, usersMap, fetchHistorial, fetchAlertas]);

  const handleLimpiar = () => {
    setSearch("");
    setFilterTipo("");
    setFilterHerramienta("");
    setFilterTaller("");
    setFilterResponsable("");
    setFilterEstado("");
  };

  const source = activeTab === "historial" ? histRows : activeRows;

  // Filter options from current data
  const uniqueHerramientas = useMemo(
    () => [
      ...new Set(
        source.map((r) => r.herramienta).filter((v) => v && v !== "Sin herramienta"),
      ),
    ],
    [source],
  );
  const uniqueTalleres = useMemo(
    () => [...new Set(source.map((r) => r.taller).filter((v) => v !== "Sin taller"))],
    [source],
  );
  const uniqueResponsables = useMemo(
    () => [
      ...new Set(source.map((r) => r.responsable).filter((v) => v !== "Sin responsable")),
    ],
    [source],
  );

  const rows = useMemo(
    () =>
      source.filter((r) => {
        const q = search.toLowerCase();
        const matchSearch =
          !q ||
          r.herramienta.toLowerCase().includes(q) ||
          r.codigoSerie.toLowerCase().includes(q) ||
          r.responsable.toLowerCase().includes(q);
        return (
          matchSearch &&
          (!filterTipo || r.tipo === filterTipo) &&
          (!filterHerramienta || r.herramienta === filterHerramienta) &&
          (!filterTaller || r.taller === filterTaller) &&
          (!filterResponsable || r.responsable === filterResponsable) &&
          (!filterEstado || r.estado === filterEstado)
        );
      }),
    [
      source,
      search,
      filterTipo,
      filterHerramienta,
      filterTaller,
      filterResponsable,
      filterEstado,
    ],
  );

  // Detalle por URL (/almacen/mantenimiento/:toolId) para que el reload no
  // regrese al listado. La fila se toma de los datos ya cargados.
  // Con ?nuevo= se abre el alta directo, sin pasar por el boton.
  useEffect(() => {
    if (nuevoToolId) setShowForm(true);
  }, [nuevoToolId]);

  // El id lo entrega el router desde la ruta /almacen/mantenimiento/:id.
  const { id: detailToolIdParam } = useParams<{ id: string }>();
  const detailToolId = detailToolIdParam ?? null;
  const detailRow = detailToolId
    ? ([...activeRows, ...histRows].find((r) => r.toolId === detailToolId) ??
      null)
    : null;

  // Deep-link inválido (herramienta no está en los datos): volver al listado.
  useEffect(() => {
    if (detailToolId && !loading && !detailRow) {
      navigate("/almacen/mantenimiento", { replace: true });
    }
  }, [detailToolId, loading, detailRow, navigate]);

  if (showForm) {
    return (
      <RegistrarEntradaForm
        initialToolId={nuevoToolId ?? undefined}
        onBack={() => {
          setShowForm(false);
          if (nuevoToolId) navigate("/almacen/mantenimiento?tab=alertas", { replace: true });
        }}
        onSubmit={() => fetchData()}
      />
    );
  }

  // Detalle de herramienta por URL: mientras carga la data muestra loader;
  // si la herramienta ya está en los datos cargados, se renderiza el detalle.
  if (detailToolId) {
    if (!detailRow) {
      return (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      );
    }
    return (
      <>
        <VerHerramientaView
          row={detailRow}
          onBack={() => navigate("/almacen/mantenimiento")}
          onRetornar={() => setRetornoRow(detailRow)}
          isHistorial={activeTab === "historial"}
        />
        {retornoRow && (
          <RetornoModal
            row={retornoRow}
            onClose={() => setRetornoRow(null)}
            onGuardar={() => {
              setRetornoRow(null);
              navigate("/almacen/mantenimiento");
              fetchData();
            }}
          />
        )}
      </>
    );
  }

  return (
    <div className="mx-auto px-8 py-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mantenimiento</h1>
          <p className="text-sm text-gray-500 mt-1">
            Herramientas enviadas a taller o servicio técnico
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className={estiloDeBoton('primario')}
        >
          <CirclePlus className="w-4 h-4" />
          Nuevo mantenimiento
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6">
        {(["mantenimiento", "alertas", "historial"] as Tab[]).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-sm font-semibold transition-colors border-b-2 ${
              activeTab === tab
                ? "border-[#477FFA]"
                : "border-transparent hover:border-gray-200"
            }`}
            style={{ color: "#1E1E1F" }}
          >
            {tab === "historial"
              ? "Historial"
              : tab === "alertas"
                ? "Alertas"
                : "En proceso"}
          </button>
        ))}
      </div>

      {activeTab === "alertas" ? (
        <AlertasTab
          rows={alertaRows}
          loading={loading || alertasLoading || !alertasLoaded}
          onRegistrar={(toolId) => navigate(`/almacen/mantenimiento?tab=alertas&nuevo=${toolId}`)}
        />
      ) : loading || (activeTab === "historial" && (histLoading || !histLoaded)) ? (
        // A-01: silueta de la tabla, igual en En proceso y en Historial.
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <EsqueletoTabla filas={6} columnas={5} />
        </div>
      ) : source.length === 0 ? (
        <NoDataState />
      ) : (
        <>
          {/* Filter bar */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap items-center gap-3 mb-6">
            <div className="relative w-[260px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar en herramientas..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B5998]"
              />
            </div>
            <FilterSelect
              label="Tipo"
              options={(["Preventivo", "Correctivo"]).map((o) => ({ value: o, label: o }))}
              value={filterTipo}
              onChange={setFilterTipo}
            ancho="contenido"
          />
            <FilterSelect
              label="Herramienta"
              options={(uniqueHerramientas).map((o) => ({ value: o, label: o }))}
              value={filterHerramienta}
              onChange={setFilterHerramienta}
            ancho="contenido"
          />
            <FilterSelect
              label="Taller"
              options={(uniqueTalleres).map((o) => ({ value: o, label: o }))}
              value={filterTaller}
              onChange={setFilterTaller}
            ancho="contenido"
          />
            <FilterSelect
              label="Responsable"
              options={(uniqueResponsables).map((o) => ({ value: o, label: o }))}
              value={filterResponsable}
              onChange={setFilterResponsable}
            ancho="contenido"
          />
            <FilterSelect
              label="Estado"
              options={(["Reparado", "En mantenimiento"]).map((o) => ({ value: o, label: o }))}
              value={filterEstado}
              onChange={setFilterEstado}
            ancho="contenido"
          />
            <button
              onClick={handleLimpiar}
              className="text-sm font-semibold text-[#315FB9] hover:underline"
            >
              Limpiar
            </button>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr
                    style={{
                      backgroundColor: "#FAF9FF",
                      borderBottom: "1px solid #E4E4E4",
                    }}
                  >
                    {[
                      "Herramienta",
                      "Tipo",
                      "Fecha de envío",
                      "Taller",
                      "Responsable",
                      activeTab === "historial"
                        ? "Estado resultante"
                        : "Estado",
                      "Núm. de serie",
                      "Acciones",
                    ].map((h) => (
                      <th
                        key={h}
                        className="text-left py-3 px-3 text-sm font-semibold first:pl-6 last:pr-6 last:text-right whitespace-nowrap"
                        style={{ color: "#534D59" }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={8}>
                        <NoResultsState />
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr
                        key={row.toolId}
                        onClick={() =>
                          navigate(`/almacen/mantenimiento/${row.toolId}`)
                        }
                        className="hover:bg-[#F4F7FB] transition-colors cursor-pointer"
                        style={{ borderBottom: "1px solid #E4E4E4" }}
                      >
                        <td className="py-3 px-3 pl-6 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-700">
                            {row.herramienta}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-gray-600 whitespace-nowrap">
                          {row.tipo}
                        </td>
                        <td className="py-3 px-3 text-gray-600 whitespace-nowrap">
                          {row.fechaEnvio}
                        </td>
                        <td className="py-3 px-3 text-gray-600 whitespace-nowrap">
                          {row.taller}
                        </td>
                        <td className="py-3 px-3 text-gray-600 whitespace-nowrap">
                          {row.responsable}
                        </td>
                        <td className="py-3 px-3 whitespace-nowrap">
                          <EstadoBadge estado={row.estado} />
                        </td>
                        <td className="py-3 px-3 text-gray-500 whitespace-nowrap">
                          {row.codigoSerie}
                        </td>
                        <td
                          className="py-3 px-3 pr-6 text-right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="inline-flex items-center gap-3">
                            <button
                              onClick={() => setComentariosRow(row)}
                              className="inline-flex items-center gap-1.5 text-sm font-medium transition-colors hover:opacity-80"
                              style={{ color: "#315FB9" }}
                            >
                              <MessageCircle className="w-4 h-4" />
                              Ver notas
                            </button>
                            {activeTab === "mantenimiento" && (
                              <button
                                onClick={() => setRetornoRow(row)}
                                className="inline-flex items-center gap-1.5 text-sm font-medium transition-colors hover:opacity-80"
                                style={{ color: "#315FB9" }}
                              >
                                <ArrowLeftToLine className="w-4 h-4" />
                                Devolver
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Comentarios modal */}
      {comentariosRow && (
        <ComentariosModal
          row={comentariosRow}
          onClose={() => setComentariosRow(null)}
        />
      )}

      {/* Retorno modal (desde la lista) */}
      {retornoRow && !detailToolId && (
        <RetornoModal
          row={retornoRow}
          onClose={() => setRetornoRow(null)}
          onGuardar={() => {
            setRetornoRow(null);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

export default MantenimientoAlmacen;
